// eslint-disable-next-line no-restricted-imports
import type { FieldValues, Path, UseFormGetValues, UseFormUnregister } from 'react-hook-form';
// eslint-disable-next-line no-restricted-imports
import { useFormContext as useRHFormContext, useWatch } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { ObjectDotNotation } from '@luna/utils-ts';

import type { CustomFieldErrors, FormValidationErrorMap } from './FormValidationError';
import { mapValuesToDotNotation } from './ValuesService';
import { get, mapErrorsToDotNotation, setError, setErrors } from './ErrorsService';
import type {
    ClearErrors,
    ServerErrorValues,
    SetError,
    SetErrors,
    SetValue,
    SetValues,
    TriggerFormValidation,
} from './type';

type ResetFieldConfig = {
    keepError?: boolean;
    keepDirty?: boolean;
    keepTouched?: boolean;
    defaultValue?: unknown;
};

type Reset<FormValues> = (values?: FormValues) => void;

type ResetField<FormValues> = (
    name: ObjectDotNotation<FormValues> | string,
    options?: ResetFieldConfig,
) => void;

type UseFormContextReturnType<FormValues extends FieldValues> = {
    setValue: SetValue<FormValues>;
    setValues: SetValues<FormValues>;
    getValues: UseFormGetValues<FormValues>;
    reset: Reset<FormValues>;
    resetField: ResetField<FormValues>;
    /**
     * Triggers validation on all fields
     *
     * @remarks
     * Use only for complex forms when you need to manually trigger form validation
     *
     * @returns promise resolving to true if the form is valid or false if invalid
     */
    triggerFormValidation: TriggerFormValidation<FormValues>;
    setErrors: SetErrors<FormValues>;
    setError: SetError<FormValues>;
    errors: FormValidationErrorMap<FormValues & ServerErrorValues>;
    clearErrors: ClearErrors<FormValues>;
    isValid: boolean;
    isSubmitted: boolean;
    isSubmitting: boolean;
    isSubmitSuccessful: boolean;
    isDirty: boolean;
    values: FormValues;
    /**
     * Removes input reference and its value
     *
     * @remarks
     * Built-in validation rules will be removed as well
     */
    unregister: UseFormUnregister<FormValues>;
    /**
     * Sets touched flag for every field currently present in the form
     */
    setTouched(): void;
};

const EMPTY_VALUES = {};

/**
 * A custom hook that provides access to the Form context.
 * It wraps react-hook-form's useFormContext and provides additional functionality.
 *
 * @template FormValues The type of the form values.
 * @param {UseFormContextParams} params Configuration options for the hook.
 * @returns {UseFormContextReturnType<FormValues>} An object containing form state and methods.
 *
 * @example
 * ```tsx
 * import { useFormContext } from '@luna/form2';
 *
 * interface FormData {
 *   email: string;
 *   password: string;
 * }
 *
 * const FormField = ({ name }: { name: keyof FormData }) => {
 *   const { values, errors, setValue } = useFormContext<FormData>();
 *
 *   return (
 *     <div>
 *       <input
 *         value={values[name]}
 *         onChange={(e) => setValue(name, e.target.value)}
 *       />
 *       {errors[name] && <span>{errors[name]}</span>}
 *     </div>
 *   );
 * };
 * ```
 */
export const useFormContext = <
    FormValues extends FieldValues,
>(): UseFormContextReturnType<FormValues> => {
    const form = useRHFormContext<FormValues>();
    const {
        setValue: setFormValue,
        formState,
        reset,
        resetField,
        getValues,
        trigger,
        unregister,
        clearErrors,
    } = form;

    const { errors, isValid, isDirty, isSubmitted, isSubmitting, isSubmitSuccessful } = formState;

    const mappedErrors = useMemo(() => {
        const { root = {}, ...rest } = errors;

        return {
            ...mapErrorsToDotNotation<FormValues & ServerErrorValues>(root as CustomFieldErrors),
            ...mapErrorsToDotNotation<FormValues>(rest as CustomFieldErrors),
        };
    }, [JSON.stringify(errors)]);

    const clearRootErrorIfNeeded = useCallback(
        (name: string) => {
            const rootErrorName = `root.${name}`;
            const rootError = get(errors, rootErrorName);

            if (rootError) {
                clearErrors(rootErrorName as any);
            }
        },
        [clearErrors, errors],
    );
    const isAfterInitialRenderRef = useRef(false);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            isAfterInitialRenderRef.current = true;
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            isAfterInitialRenderRef.current = false;
        };
    }, []);

    const setValue: SetValue<FormValues> = useCallback(
        (key, value, options) => {
            clearRootErrorIfNeeded(key);

            if (!isAfterInitialRenderRef.current) {
                // https://github.com/react-hook-form/react-hook-form/issues/11056
                // Prevents useWatchFieldValue not working when setting values in
                // initial useEffect()
                setTimeout(() => {
                    setFormValue(key as Path<FormValues>, value as any, options);
                }, 0);
                return;
            }

            setFormValue(key as Path<FormValues>, value as any, options);
        },
        [clearRootErrorIfNeeded, setFormValue],
    );

    const setTouched = useCallback(() => {
        const latestValues = mapValuesToDotNotation<FormValues>(getValues());

        Object.entries(latestValues).forEach(([key, value]) => {
            setValue(key, value, { shouldTouch: true });
        });
    }, [getValues, setValue]);

    const setValues: SetValues<FormValues> = useCallback(
        (formValues, options) => {
            Object.keys(formValues).forEach((key) => {
                const value = formValues[key];
                setValue(key, value, options);
            });
        },
        [setValue],
    );

    const handleErrors: SetErrors<FormValues> = useCallback(
        (errorsToSet) => setErrors(form, errorsToSet),
        [form],
    );

    const handleSetError: SetError<FormValues> = useCallback(
        (fieldName, { errorType, message, shouldFocus }) =>
            setError(form, fieldName, { errorType, message, shouldFocus }),
        [form],
    );

    return {
        setValue: setValue as SetValue<FormValues>,
        setValues,
        getValues,
        reset,
        resetField: resetField as ResetField<FormValues>,
        triggerFormValidation: trigger as TriggerFormValidation<FormValues>,
        values: EMPTY_VALUES as FormValues,
        setErrors: handleErrors,
        setError: handleSetError,
        errors: mappedErrors,
        clearErrors: clearErrors as ClearErrors<FormValues>,
        isDirty,
        isValid,
        isSubmitted,
        isSubmitting,
        isSubmitSuccessful,
        unregister,
        setTouched,
    };
};

export const useWatchFormContext = <
    FormValues extends FieldValues,
>(): UseFormContextReturnType<FormValues> => {
    return {
        ...useFormContext<FormValues>(),
        values: useWatch() as FormValues,
    };
};
